// A patient's insurance cover (docs/plans/phase2-private-insurance.md §6 items 1 and 2):
// the section on the patient page, the dialog that records a card, and the choice of
// cover at check-in. Member numbers are identifiers, in the mono face.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { COVER_VALIDITY_WORDS } from '@wodi/domain';
import type { CoverageView, RecordCoverageRequest } from '@wodi/shared';
import {
  Button,
  DateField,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  RadioGroup,
  Select,
  Tag,
  TextField,
  type TagState,
} from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import { payerQuery, payersQuery } from '../api/finance.ts';
import { changeCoverage, coveragesQuery, recordCoverage } from '../api/insurance.ts';
import { useOnline } from '../hooks/use-online.ts';
import { formatIsoDate } from '../patients/format.ts';
import { ReasonDialog } from '../patients/reason-dialog.tsx';
import { Section } from '../patients/record-sections.tsx';
import { toasts } from '../toasts.ts';
import styles from './insurance.module.css';

const VALIDITY_TAG: Record<CoverageView['validity'], TagState> = {
  valid: 'active',
  not_started: 'pending',
  expired: 'neutral',
  ended: 'neutral',
};

export function coverLine(cover: CoverageView): string {
  return `${cover.payerName}, ${cover.schemeName}`;
}

function periodText(cover: CoverageView): string {
  if (cover.periodStart === null) return 'No dates given';
  const from = formatIsoDate(cover.periodStart);
  return cover.periodEnd === null
    ? `From ${from}, no end date`
    : `${from} to ${formatIsoDate(cover.periodEnd)}`;
}

function CoverRow({ cover, canManage }: { cover: CoverageView; canManage: boolean }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [action, setAction] = useState<'end' | 'entered-in-error' | null>(null);
  const change = useMutation({
    mutationFn: (reason: string) =>
      changeCoverage(cover.id, action ?? 'end', { expectedVersion: cover.version, reason }),
    onSuccess: async () => {
      setAction(null);
      toasts.show({ title: action === 'end' ? 'Cover ended' : 'Cover marked entered in error' });
      await queryClient.invalidateQueries({ queryKey: ['patients'] });
    },
  });
  const isInsurer = cover.source === 'recorded';
  return (
    <li className={styles.row}>
      <div className={styles.grow}>
        <div className={styles.sentence}>{coverLine(cover)}</div>
        <div className={styles.detail}>
          {cover.memberNumber === null ? null : (
            <>
              {'Member '}
              <span className={styles.id}>{cover.memberNumber}</span>
              {' · '}
            </>
          )}
          {cover.relationship === 'dependant'
            ? `Dependant of ${cover.principalName ?? 'the principal'}, `
            : cover.relationship === 'principal'
              ? 'Principal member, '
              : ''}
          {cover.relationship === 'dependant' && cover.principalMemberNumber !== null ? (
            <>
              {'principal '}
              <span className={styles.id}>{cover.principalMemberNumber}</span>
              {' · '}
            </>
          ) : null}
          {periodText(cover)}
        </div>
        {cover.statusReason === null ? null : (
          <div className={styles.detail}>{`Reason: ${cover.statusReason}`}</div>
        )}
      </div>
      <Tag state={VALIDITY_TAG[cover.validity]}>
        {cover.status === 'entered_in_error'
          ? 'Entered in error'
          : COVER_VALIDITY_WORDS[cover.validity]}
      </Tag>
      {canManage && isInsurer && cover.status === 'active' ? (
        <div className={styles.actions}>
          <Button
            dense
            isDisabled={!online}
            onPress={() => {
              setAction('end');
            }}
          >
            End cover
          </Button>
          <Button
            dense
            variant="quiet"
            isDisabled={!online}
            onPress={() => {
              setAction('entered-in-error');
            }}
          >
            Entered in error
          </Button>
        </div>
      ) : null}
      <ReasonDialog
        title={action === 'end' ? 'End this cover' : 'Mark this cover entered in error'}
        isOpen={action !== null}
        onOpenChange={(open) => {
          if (!open) setAction(null);
        }}
        confirmLabel={action === 'end' ? 'End cover' : 'Mark entered in error'}
        pendingLabel="Saving…"
        isPending={change.isPending}
        error={change.error instanceof ApiError ? change.error.message : null}
        onConfirm={(reason) => {
          change.mutate(reason);
        }}
      />
    </li>
  );
}

/** The insurer schemes a card can be recorded against, as select options. */
function useInsurerSchemes() {
  const payers = useQuery(payersQuery);
  const insurers = (payers.data ?? []).filter((p) => p.kind === 'insurer' && p.status === 'active');
  return { payers, insurers };
}

function SchemeSelect({
  payerId,
  schemeId,
  onChange,
}: {
  payerId: string;
  schemeId: string | null;
  onChange: (id: string | null) => void;
}) {
  const detail = useQuery(payerQuery(payerId));
  const schemes = (detail.data?.schemes ?? []).filter((s) => s.status === 'active');
  if (detail.isPending) return <LoadingBlock label="Loading schemes" rows={1} />;
  if (schemes.length === 0)
    return (
      <Notice tone="info">
        This insurer has no schemes set up. Finance adds them in Settings, under Payers.
      </Notice>
    );
  return (
    <Select
      label="Scheme"
      options={schemes.map((s) => ({ id: s.id, label: s.name }))}
      value={schemeId}
      onChange={(v) => {
        onChange(v === null ? null : String(v));
      }}
    />
  );
}

function AddCoverDialog({
  patientId,
  isOpen,
  onOpenChange,
}: {
  patientId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { payers, insurers } = useInsurerSchemes();
  const [payerId, setPayerId] = useState<string | null>(null);
  const [schemeId, setSchemeId] = useState<string | null>(null);
  const [memberNumber, setMemberNumber] = useState('');
  const [relationship, setRelationship] = useState<'principal' | 'dependant'>('principal');
  const [principalMemberNumber, setPrincipalMemberNumber] = useState('');
  const [principalName, setPrincipalName] = useState('');
  const [periodStart, setPeriodStart] = useState<string | null>(null);
  const [periodEnd, setPeriodEnd] = useState<string | null>(null);
  const [tried, setTried] = useState(false);

  const missing =
    schemeId === null ||
    memberNumber.trim() === '' ||
    periodStart === null ||
    (relationship === 'dependant' && principalMemberNumber.trim() === '');
  const save = useMutation({
    mutationFn: () => {
      const body: RecordCoverageRequest = {
        schemeId: schemeId ?? '',
        memberNumber: memberNumber.trim(),
        relationship,
        periodStart: periodStart ?? '',
        ...(periodEnd === null ? {} : { periodEnd }),
        ...(relationship === 'dependant'
          ? {
              principalMemberNumber: principalMemberNumber.trim(),
              ...(principalName.trim() === '' ? {} : { principalName: principalName.trim() }),
            }
          : {}),
      };
      return recordCoverage(patientId, body);
    },
    onSuccess: async () => {
      toasts.show({ title: 'Cover recorded' });
      await queryClient.invalidateQueries({ queryKey: ['patients', patientId] });
      onOpenChange(false);
    },
  });

  return (
    <Dialog title="Add insurance cover" isOpen={isOpen} onOpenChange={onOpenChange}>
      <form
        className={styles.form}
        aria-label="Add insurance cover"
        onSubmit={(e) => {
          e.preventDefault();
          setTried(true);
          if (!missing) save.mutate();
        }}
      >
        {payers.isPending ? <LoadingBlock label="Loading insurers" rows={2} /> : null}
        {payers.isSuccess && insurers.length === 0 ? (
          <Notice tone="info">
            No insurers are set up yet. Finance adds them in Settings, under Payers.
          </Notice>
        ) : null}
        {insurers.length > 0 ? (
          <Select
            label="Insurer"
            options={insurers.map((p) => ({ id: p.id, label: p.name }))}
            value={payerId}
            onChange={(v) => {
              setPayerId(v === null ? null : String(v));
              setSchemeId(null);
            }}
          />
        ) : null}
        {payerId === null ? null : (
          <SchemeSelect payerId={payerId} schemeId={schemeId} onChange={setSchemeId} />
        )}
        <TextField
          label="Member number"
          description="As printed on the card."
          value={memberNumber}
          onChange={setMemberNumber}
          {...(tried && memberNumber.trim() === ''
            ? { errorMessage: 'Enter the member number from the card.' }
            : {})}
        />
        <RadioGroup
          label="The patient is"
          orientation="horizontal"
          value={relationship}
          onChange={(v) => {
            setRelationship(v === 'dependant' ? 'dependant' : 'principal');
          }}
          options={[
            { id: 'principal', label: 'The principal member' },
            { id: 'dependant', label: 'A dependant' },
          ]}
        />
        {relationship === 'dependant' ? (
          <div className={styles.fields}>
            <TextField
              label="Principal's member number"
              value={principalMemberNumber}
              onChange={setPrincipalMemberNumber}
              {...(tried && principalMemberNumber.trim() === ''
                ? { errorMessage: "Enter the principal member's number." }
                : {})}
            />
            <TextField
              label="Principal's name"
              description="Optional, as on the card."
              value={principalName}
              onChange={setPrincipalName}
            />
          </div>
        ) : null}
        <div className={styles.fields}>
          <DateField
            label="Valid from"
            value={periodStart}
            onChange={setPeriodStart}
            {...(tried && periodStart === null
              ? { errorMessage: 'Enter the date the cover starts.' }
              : {})}
          />
          <DateField
            label="Valid to"
            description="Leave empty when the card has no end date."
            value={periodEnd}
            onChange={setPeriodEnd}
            {...(periodStart !== null && periodEnd !== null ? { minValue: periodStart } : {})}
          />
        </div>
        {tried && schemeId === null ? (
          <Notice tone="error">Choose the insurer and scheme on the card.</Notice>
        ) : null}
        {save.error === null ? null : (
          <Notice tone="error">
            {save.error instanceof ApiError
              ? save.error.message
              : "The cover couldn't be recorded. Try again."}
          </Notice>
        )}
        <DialogActions>
          <Button
            variant="quiet"
            onPress={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" isPending={save.isPending} pendingLabel="Saving…">
            Record cover
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

/** The patient page's "Insurance cover" section. */
export function InsuranceCoverSection({
  patientId,
  canManage,
}: {
  patientId: string;
  canManage: boolean;
}) {
  const online = useOnline();
  const covers = useQuery(coveragesQuery(patientId));
  const [adding, setAdding] = useState(false);
  return (
    <Section
      title="Insurance cover"
      {...(canManage
        ? {
            action: (
              <Button
                dense
                isDisabled={!online}
                onPress={() => {
                  setAdding(true);
                }}
              >
                Add insurance cover
              </Button>
            ),
          }
        : {})}
    >
      {covers.isPending ? <LoadingBlock label="Loading cover" rows={2} /> : null}
      {covers.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void covers.refetch()}>
              Try again
            </Button>
          }
        >
          {covers.error instanceof ApiError
            ? covers.error.message
            : "The patient's cover couldn't be loaded. Try again."}
        </Notice>
      ) : null}
      {covers.isSuccess && covers.data.items.length === 0 ? (
        <p className={styles.detail}>
          No insurance cover recorded. Cover from a private insurer's card is added here; SHA cover
          comes from the check at reception.
        </p>
      ) : null}
      {covers.isSuccess && covers.data.items.length > 0 ? (
        <ul className={styles.list} aria-label="Insurance cover">
          {covers.data.items.map((c) => (
            <CoverRow key={c.id} cover={c} canManage={canManage} />
          ))}
        </ul>
      ) : null}
      {adding ? (
        <AddCoverDialog patientId={patientId} isOpen={adding} onOpenChange={setAdding} />
      ) : null}
    </Section>
  );
}

/**
 * The choice of cover at check-in: the patient's valid private insurer covers as radios.
 * None: says so, with the way to add one.
 */
export function InsurerCoverChoice({
  patientId,
  coverageId,
  onSelect,
}: {
  patientId: string;
  coverageId: string | null;
  onSelect: (coverageId: string | null) => void;
}) {
  const covers = useQuery(coveragesQuery(patientId));
  if (covers.isPending) return <LoadingBlock label="Loading the patient's cover" rows={2} />;
  if (covers.isError)
    return (
      <Notice
        tone="error"
        action={
          <Button dense onPress={() => void covers.refetch()}>
            Try again
          </Button>
        }
      >
        {covers.error instanceof ApiError
          ? covers.error.message
          : "The patient's cover couldn't be loaded. Try again."}
      </Notice>
    );
  const insurer = covers.data.items.filter((c) => c.source === 'recorded' && c.status === 'active');
  const valid = insurer.filter((c) => c.validity === 'valid');
  if (valid.length === 0)
    return (
      <Notice
        tone="info"
        action={
          <Link to="/patients/$patientId" params={{ patientId }}>
            Open the patient record
          </Link>
        }
      >
        {insurer.length === 0
          ? 'No insurance cover recorded. Add it on the patient page, or check in as cash.'
          : 'The recorded cover is not valid today. Check the card and record the current cover, or check in as cash.'}
      </Notice>
    );
  return (
    <RadioGroup
      label="Insurance cover"
      value={coverageId ?? ''}
      onChange={(v) => {
        onSelect(v === '' ? null : v);
      }}
      options={valid.map((c) => ({
        id: c.id,
        label: `Bill to ${coverLine(c)}, member ${c.memberNumber ?? ''}`,
      }))}
    />
  );
}
